import {
  READ_AUDIT_LOG,
  type GrantablePermission,
  type PaidModule,
  type PermissionArea,
} from '@palaccount/contracts';
import {
  Banknote,
  Briefcase,
  Calculator,
  ChartColumn,
  Factory,
  FolderKanban,
  Home,
  Settings,
  Shield,
  ShoppingBag,
  ShoppingCart,
  Users,
  Warehouse,
  type LucideIcon,
} from 'lucide-react';

/**
 * The 13 side-menu groups of spec §4.3.1, in the prototype's order. Labels are
 * i18n keys whose EN/AR strings are the prototype dictionary's own (the spec
 * forbids reconstructing labels from key names).
 *
 * ── WHAT MAKES A GROUP APPEAR ──────────────────────────────────────────────
 *
 * Two independent questions, and §1.5.2 fixes the order in which they are
 * asked: "permission is checked first, then the module flag." Both answers can
 * only ever make a group ABSENT — never greyed, never a group that opens onto
 * a refusal:
 *
 *   `areas`  — BR-PERM-19: "A menu section a role cannot view must be absent,
 *              not greyed." BR-PERM-43 states the same rule from the other
 *              side: "An area a role holds nothing on must have its menu group
 *              hidden entirely… For a user with no role, every menu group is
 *              hidden on the same rule." A group listing no area is one every
 *              session may reach.
 *
 *   `module` — §1.5.3: Manufacturing off removes "the whole Manufacturing /
 *              Production menu group, and its four screens"; Projects the
 *              same. "A switched-off module is off for the whole company and
 *              for every user in it, whatever their role. No permission brings
 *              it back."
 *
 * **ABSENT AND REFUSED ARE DIFFERENT STATEMENTS, and the difference is the
 * point of this file.** A module the company never bought is absent, because
 * §1.5.3 says the group disappears; a screen the role cannot open is also
 * absent, by BR-PERM-19. Neither is a *check*. BR-PERM-51 is explicit that
 * "Navigation filtering is a display convenience. Every route must enforce its
 * own permission when it is opened… A route must never rely on its menu entry
 * being hidden", and BR-PERM-50 adds that "Hiding a control must never be the
 * only thing that prevents an action." PermissionGuard and EntitlementsGuard
 * on the server are the control and are untouched by any of this. What this
 * table changes is whether the interface tells the truth about the product the
 * person is standing in.
 */
export interface NavGroup {
  id: string;
  /** URL path. Home is '/', the default landing screen (GEN-2a). */
  to: string;
  labelKey: string;
  /** Page title key for the group's landing page (GEN-11). */
  pageKey: string;
  icon: LucideIcon;
  /**
   * BR-PERM-19 / BR-PERM-43 — the group is present when the role holds ANY
   * grant in ANY of these areas. Empty means "everyone with a session".
   *
   * ANY grant rather than specifically `<area>.view`, on purpose. BR-PERM-19
   * is worded around View and BR-PERM-43 around holding "nothing on" the area;
   * the two coincide for every sane grid, and diverge only for a role holding
   * `sales.create` without `sales.view` — which BR-PERM-01 forbids and this
   * build does not yet enforce (acceptance finding C15). Until it does, the
   * kinder reading wins: a person is never hidden from work they can actually
   * do, and the server refuses whatever they cannot.
   */
  areas: readonly PermissionArea[];
  /**
   * Grants outside the 12x5 grid that also open the group — a screen whose
   * permission is a named extension rather than an area action. The Reports
   * group carries the Audit Log (DSH-09 §3: "the fifth and last entry in the
   * Reports group"), whose read is its own permission (BR-DSH-09-13).
   */
  extras?: readonly GrantablePermission[];
  /** §1.5.3 — absent unless the company bought this module. */
  module?: PaidModule;
  /**
   * The menu draws a hairline ABOVE this group: the operational groups
   * (Sales … Reports) end here and the administrative ones (Settings, Admin)
   * begin. Drawn only when a visible group precedes it, so a role that sees
   * Home and Admin alone still gets one divider and a role that reaches no
   * admin group gets none.
   */
  startsSection?: true;
}

export const NAV_GROUPS: readonly NavGroup[] = [
  // Home is the default landing screen (GEN-2a) and is listed by NO area on
  // purpose: PART 16.7 puts the role-less user's explanation ON the landing
  // page — "say why on the landing page rather than showing an empty shell"
  // (BR-PERM-73) — which requires them to have a landing page to be on. Its
  // widgets are gated server-side on `reports.view` like every other read.
  { id: 'home', to: '/', labelKey: 'shell.navHome', pageKey: 'shell.pageFinDash', icon: Home, areas: [] },
  { id: 'sales', to: '/sales', labelKey: 'shell.navSales', pageKey: 'shell.navSales', icon: ShoppingCart, areas: ['sales'] },
  { id: 'purchases', to: '/purchases', labelKey: 'shell.navPurchases', pageKey: 'shell.navPurchases', icon: ShoppingBag, areas: ['purchasing'] },
  // The one gate above permissions (§9.1.2) as well as its own area —
  // EC-19-63 (retired by R1): whether Manufacturing appears is decided by
  // the module flag and the `manufacturing` permission area, and by nothing
  // else; the Advanced-only layouts the case named are gone with the mode.
  // GEN-18 in the same breath: menu visibility has exactly two inputs — the
  // company's module flags and the role's permissions (visibleGroups reads
  // nothing else) — and GEN-202's Manufacturing/Projects group gating rides
  // the `module` fields on these two rows, with GEN-202a's Multi-branch
  // hiding only its two widgets in the dashboard catalog. The mode-era
  // rules GEN-16, GEN-18b, GEN-19, GEN-20, GEN-20a and GEN-21 are RETIRED
  // by owner ruling R1 (no view mode exists to remember, switch, or hide
  // behind), as are R3's GEN-40a and GEN-40c (no document-type selector).
  { id: 'manufacturing', to: '/manufacturing', labelKey: 'shell.navManufacturing', pageKey: 'shell.navManufacturing', icon: Factory, areas: ['manufacturing'], module: 'manufacturing' },
  { id: 'inventory', to: '/inventory', labelKey: 'shell.navInventory', pageKey: 'shell.navInventory', icon: Warehouse, areas: ['inventory'] },
  { id: 'partners', to: '/partners', labelKey: 'shell.navPartners', pageKey: 'shell.navPartners', icon: Users, areas: ['crm'] },
  { id: 'accounting', to: '/accounting', labelKey: 'shell.navAccounting', pageKey: 'shell.navAccounting', icon: Calculator, areas: ['accounting'] },
  { id: 'banks', to: '/banks', labelKey: 'shell.navBanks', pageKey: 'shell.navBanks', icon: Banknote, areas: ['banking'] },
  // `payroll` is its own area (16.2.3, R15) but shares the group: an account
  // holding payroll and nothing else still needs the door to HR-02.
  { id: 'employees', to: '/employees', labelKey: 'shell.navEmployees', pageKey: 'shell.navEmployees', icon: Briefcase, areas: ['hr', 'payroll'] },
  { id: 'projects', to: '/projects', labelKey: 'shell.navProjects', pageKey: 'shell.navProjects', icon: FolderKanban, areas: ['projects'], module: 'projects' },
  { id: 'reports', to: '/reports', labelKey: 'shell.navReports', pageKey: 'shell.navReports', icon: ChartColumn, areas: ['reports'], extras: [READ_AUDIT_LOG] },
  { id: 'settings', to: '/settings', labelKey: 'shell.navSettings', pageKey: 'shell.navSettings', icon: Settings, areas: ['admin'], startsSection: true },
  { id: 'admin', to: '/admin', labelKey: 'shell.navAdmin', pageKey: 'shell.navAdmin', icon: Shield, areas: ['admin'] },
] as const;

/**
 * THE PERSONAL SCREENS — the ones that belong to a PERSON rather than to a
 * menu group, and therefore have no group to take a breadcrumb from.
 *
 * ADM-04 §3: the bell "works for everybody, whatever their role", and ADM-08
 * §3 says the same of the sliders button and adds "There is NO MENU ENTRY. The
 * breadcrumb reads PREFERENCES". Both are reached from the top bar and both
 * are `@SelfService` on the server — PART 16.1's rung-4 floor, which
 * BR-PERM-73 keeps for an account holding no role at all.
 *
 * They are NOT nav groups: BR-PERM-19 and BR-PERM-43 are about hiding groups a
 * role cannot use, and these two are the screens no role can be missing. What
 * they need from this file is a breadcrumb and a title, which is all this map
 * is.
 */
export const PERSONAL_PAGES: Readonly<Record<string, { labelKey: string; pageKey: string }>> = {
  '/notifications': { labelKey: 'notif.crumb', pageKey: 'notif.title' },
  // ADM-08 §3: "There is NO MENU ENTRY. The breadcrumb reads PREFERENCES."
  '/personalization': { labelKey: 'personalize.crumb', pageKey: 'personalize.title' },
  // UB-03 — the Help page, reached from the top bar's Help / Contact buttons.
  // Personal in the same sense: every session, no group, no menu entry.
  '/help': { labelKey: 'help.crumb', pageKey: 'help.title' },
};

export function groupForPath(pathname: string): NavGroup | undefined {
  if (pathname === '/') return NAV_GROUPS[0];
  return NAV_GROUPS.find((group) => group.to !== '/' && pathname.startsWith(group.to));
}

/**
 * The breadcrumb + title for any signed-in screen, group or personal — and
 * `to`, the section's own home, which the title links to (bugs: Upper Bar —
 * "make the title clickable, as a way back").
 */
export function headingForPath(pathname: string): { labelKey: string; pageKey: string; to: string } {
  const personal = PERSONAL_PAGES[pathname];
  if (personal) return { ...personal, to: pathname };
  const group = groupForPath(pathname) ?? NAV_GROUPS[0]!;
  return { labelKey: group.labelKey, pageKey: group.pageKey, to: group.to };
}
